import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  digests: [],
  currentDigest: null,
}

export const selectDigestByTitleForMailbox = (digests, title, mailIntegrationId = null) => {
  const candidates = (digests ?? []).filter((digest) => digest.title === title);
  if (mailIntegrationId) {
    return candidates.find((digest) => digest.mailbox?.id === mailIntegrationId) ?? null;
  }
  return candidates.length === 1 ? candidates[0] : null;
};

export const selectDigestForRoute = (digests, title, preferredMailIntegrationId = null) => {
  const candidates = (digests ?? []).filter((digest) => digest.title === title);
  if (preferredMailIntegrationId) {
    const preferred = candidates.find(
      (digest) => digest.mailbox?.id === preferredMailIntegrationId
    );
    if (preferred) return preferred;
  }

  const activeCandidates = candidates.filter((digest) => digest.mailbox?.isActive === true);
  if (activeCandidates.length === 1) return activeCandidates[0];
  return candidates.length === 1 ? candidates[0] : null;
};

const digestSlice = createSlice({
  name: 'digestList',
  initialState,
  reducers: {
    setDigests: (state, action) => {
      state.digests = action.payload;
    },
    setDigestList: (state, action) => {
      action.payload.forEach((digest) => {
        // A title is not an identity: two connected mailboxes can both have a
        // digest for the same day.
        const exists = state.digests.some(
          (d) => d.id === digest.id
        );
        if (!exists) {
          state.digests.push(digest);
        }
      });
    },
    addOrSyncDigest: (state, action) => {
      const { emailSummary, taskSummary, content, labels, mailbox, destinations, updated_at } = action.payload;
      const existingDigestIndex = state.digests.findIndex(d => d.id === action.payload.id);
      
      if (existingDigestIndex !== -1) {
        const existingDigest = state.digests[existingDigestIndex];
        const resolvedDestinations = destinations !== undefined ? destinations : existingDigest.destinations;
        const resolvedMailbox = mailbox !== undefined ? mailbox : existingDigest.mailbox;
        const serverHead = existingDigest.serverHead || {};
        // Handle case where existing digest has no content or empty content array
        const existingContent = Array.isArray(existingDigest.content) && existingDigest.content.length > 0 
          ? existingDigest.content 
          : [];
        
        // If existing digest has no content, treat it as a new digest
        if (existingContent.length === 0) {
          const updatedDigest = {
            ...existingDigest,
            emailSummary,
            taskSummary,
            content,
            labels,
            mailbox: resolvedMailbox,
            destinations: resolvedDestinations,
            serverHead,
            updated_at: updated_at ?? existingDigest.updated_at,
          };
          state.digests[existingDigestIndex] = updatedDigest;
          state.currentDigest = updatedDigest;
          return;
        }
        
        // Otherwise proceed with normal merge
        const updatedContent = mergeEmailContent(existingContent, content);
        const updatedDigest = {
          ...existingDigest,
          emailSummary,
          taskSummary,
          content: updatedContent,
          labels,
          mailbox: resolvedMailbox,
          destinations: resolvedDestinations,
          serverHead,
          updated_at: updated_at ?? existingDigest.updated_at,
        };
        state.digests[existingDigestIndex] = updatedDigest;
        state.currentDigest = updatedDigest;
      } else {
        // Create new digest if it doesn't exist
        const newDigest = {
          ...action.payload,
          step: 'created',
          serverHead: action.payload.serverHead || {},
        };
        state.digests.push(newDigest);
        state.currentDigest = newDigest;
      }
    },
    updateDigest: (state, action) => {
      const digestIndex = state.digests.findIndex(d => d.id === action.payload.id)
      // keep any existing serverHead if caller didn’t set it
      const prev = digestIndex !== -1 ? state.digests[digestIndex] : null;
      const next = {
        ...action.payload,
        serverHead: action.payload.serverHead ?? prev?.serverHead,
      };
      state.currentDigest = next;

      if (digestIndex !== -1) {
        state.digests[digestIndex] = next;
      }
    },
    updateDigestContentItem: (state, action) => {
      const { digestId, contentId, updates } = action.payload;
      
      const digestIndex = state.digests.findIndex(d => d.id === digestId);
      if (digestIndex === -1) return;
      
      const digest = state.digests[digestIndex];
      const contentIndex = digest.content.findIndex(item => item.id === contentId);
      if (contentIndex === -1) return;
      
      // Update the specific content item
      digest.content[contentIndex] = {
        ...digest.content[contentIndex],
        ...updates
      };
      
      // Update current digest if it's the same one
      if (state.currentDigest?.id === digestId) {
        state.currentDigest = digest;
      }
    },
    resetProjectCreation: (state) => {
      state.currentDigest = {}
      state.currentDigest.step = ''
    },
    setCurrentDigest: (state, action) => {
      state.currentDigest = state.digests?.find(d => d.id === action.payload?.id) || null
    },
    setDigestServerHead: (state, action) => {
      const { digestId, head } = action.payload;
      const digestIndex = state.digests.findIndex(d => (digestId && d.id === digestId));
      if (digestIndex === -1) return;
    
      const prev = state.digests[digestIndex].serverHead || {};
      const next = {
        ...prev,
        ...head,
        lastEventAt: head.lastEventAt ?? Date.now(),
      };
      state.digests[digestIndex].serverHead = next;
    
      if (state.currentDigest?.id === state.digests[digestIndex].id) {
        state.currentDigest = { ...state.currentDigest, serverHead: next };
      }
    },
    // Note: This reducer is kept for compatibility but async logic is handled in App.js
    checkDigestServerHeadAndfetch: (state, action) => {
      // This reducer is intentionally empty - async logic is handled in App.js
      // The action is dispatched to trigger the async fetch logic
    },
    // Custom Folders & Smart Email Routing (D-08): a dedicated destination-
    // only refresh. Scoped by mailIntegrationId (not digest id) so a late
    // response from a mailbox the user has since switched away from is
    // discarded by its caller before ever reaching this reducer, and any
    // digest still bound to the SAME mailbox picks up the refreshed list.
    // Deliberately does not touch content/summary or trigger a sync.
    setIntegrationDestinations: (state, action) => {
      const { mailIntegrationId, destinations } = action.payload;
      if (typeof mailIntegrationId !== 'string' || mailIntegrationId.length === 0) return;

      state.digests = state.digests.map((digest) => (
        digest.mailbox?.id === mailIntegrationId
          ? { ...digest, destinations }
          : digest
      ));

      if (state.currentDigest?.mailbox?.id === mailIntegrationId) {
        state.currentDigest = { ...state.currentDigest, destinations };
      }
    },
  },
})

export const SYNCED_EMAIL_FIELDS = [
  'labels',
  'isUnread',
  'isInInbox',
  'isArchived',
  'triageDestination',
  'provider',
  'webLink',
  'threadId',
  'subject',
  'from',
  'date',
  'snippet',
];

// Helper function to merge email content
const mergeEmailContent = (existingContent, newContent) => {
  const emailMap = new Map();

  // Add existing emails to map
  existingContent.forEach(email => {
    emailMap.set(email.id, email);
  });

  // Update or add new emails
  newContent.forEach(email => {
    const existingEmail = emailMap.get(email.id);
    if (existingEmail) {
      const refreshed = { ...existingEmail };
      SYNCED_EMAIL_FIELDS.forEach((field) => {
        if (email[field] !== undefined) {
          refreshed[field] = email[field];
        }
      });
      emailMap.set(email.id, refreshed);
    } else {
      // Add new email
      emailMap.set(email.id, email);
    }
  });

  return Array.from(emailMap.values());
};


export const { 
  addOrSyncDigest, 
  updateDigest, 
  setCurrentDigest, 
  resetDigestCreation,
  updateDigestContentItem,
  setDigestList,
  setDigestServerHead,
  checkDigestServerHeadAndfetch,
  setIntegrationDestinations,
} = digestSlice.actions

export default digestSlice.reducer;
